import {
  ChangeDetectionStrategy,
  Component,
  effect,
  HostListener,
  inject,
  input,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {SearchWidgetThemeEnum} from '../../enums/search-widget-theme.enum';
import {SearchService} from '../../../../services/search/search.service';
import {SearchWidgetState} from '../../typings/search-widget-state';
import {SearchWidgetManagerService} from '../../../../services/search-widget/search-widget-manager.service';
import {UntypedFormBuilder} from '@angular/forms';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {GtmEventEnum} from '../../../../enums/GoogleEnums';
import {EventActionEnum, EventCategoryEnum} from '../../../../enums/GaTrackingEnums';
import {GtmService} from '../../../../services/gtm.service';
import {WidgetCssClassNameEnum} from '../../enums/search-widget-css-class-name.enum';
import {ToolsService} from '../../../../services/tools.service';

enum ScrollDirectionEnum {
  UP = 'up',
  STATIC = 'static',
  DOWN = 'down',
}

@Component({
  selector: 'app-search-widget-v4-mobile',
  templateUrl: './search-widget-v4-mobile.component.html',
  styleUrls: ['./search-widget-v4-mobile.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchWidgetV4MobileComponent implements OnInit {
  isOnTop = input<boolean>();
  theme = input<SearchWidgetThemeEnum>(SearchWidgetThemeEnum.DEFAULT);
  mainCareProviderTypeId = input<number>();
  inferredCareProviderTypeAreaId = input<number>();
  inferredCategoryTypeId = input<number>();
  pageType = input<DLPageTypeEnum>();
  hideFiltersAndPlacePanel = input<boolean>();

  public SearchWidgetThemeEnum = SearchWidgetThemeEnum;
  public isTermFreeSearchNotEmpty = signal<boolean>(false);
  public scrollingStatus = signal<ScrollDirectionEnum>(ScrollDirectionEnum.STATIC);
  public widgetCssClassName = signal<string>(`${WidgetCssClassNameEnum.TOP} ${WidgetCssClassNameEnum.RELATIVE}`);
  protected freeSearchForm;
  private lastScrollTop = signal<number>(0);

  private searchService = inject(SearchService);
  public fixedWidget = this.searchService.fixedWidget;
  public isMobileWidgetExpanded = this.searchService.isMobileWidgetExpanded;
  public state = this.searchService.searchWidgetState;

  constructor(
    private searchWidgetManagerService: SearchWidgetManagerService,
    private formBuilder: UntypedFormBuilder,
    private gtmService: GtmService,
    private tools: ToolsService,
  ) {
    this.buildFreeSearchForm();

    effect(() => {
      this.fixedWidget();
    });

    effect(
      () => {
        this.updateFreeSearchFieldValues(this.state());
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        const position: WidgetCssClassNameEnum =
          this.fixedWidget() === true ? WidgetCssClassNameEnum.FIXED : WidgetCssClassNameEnum.RELATIVE;
        if (!this.tools.isNil(this.theme()) && this.theme() !== SearchWidgetThemeEnum.DEFAULT) {
          this.widgetCssClassName.set(`${this.theme()} ${position}`);
        } else {
          if (this.isOnTop() === true && this.scrollingStatus() !== ScrollDirectionEnum.DOWN) {
            this.widgetCssClassName.set(`${WidgetCssClassNameEnum.TOP} ${position}`);
          } else {
            this.widgetCssClassName.set(
              `${
                this.scrollingStatus() === ScrollDirectionEnum.UP
                  ? WidgetCssClassNameEnum.UP
                  : WidgetCssClassNameEnum.NORMAL
              } ${position}`,
            );
          }
        }
      },
      {allowSignalWrites: true},
    );
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event) {
    const verticalOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    let scrollDirection: ScrollDirectionEnum;
    if (verticalOffset < this.lastScrollTop()) {
      scrollDirection = ScrollDirectionEnum.UP;
    } else if (verticalOffset > this.lastScrollTop()) {
      scrollDirection = ScrollDirectionEnum.DOWN;
    } else {
      scrollDirection = ScrollDirectionEnum.STATIC;
    }

    this.scrollingStatus.set(scrollDirection);
    this.lastScrollTop.set(verticalOffset <= 0 ? 0 : verticalOffset);
  }

  public ngOnInit(): void {
    this.searchWidgetManagerService.softResetWidgetState();
    this.searchWidgetManagerService.updateValuesInSearchWidgetState();
  }

  public onCategoriesClick(): void {
    this.searchService.mobileCategoriesPanelOpen.set(true);
  }

  public onFiltersClick(event: Event): void {
    this.searchService.mobileFiltersPanelOpen.set(true);
  }

  public onPlaceClick(event: Event): void {
    this.searchService.isMobileWidgetExpanded.set(true);
    this.searchWidgetManagerService.setFocusOnPlacesField();
  }

  protected onFreeSearchClear(): void {
    this.freeSearchForm.reset({
      term: '',
    });

    // this.searchWidgetManagerService.clearFreeSearchTerm(this.searchService.isMobileWidgetExpanded$.getValue());
    this.searchWidgetManagerService.clearFreeSearchTerm(true); //place is no longer merged with free search VS-6708
  }

  protected onFreeSearchFocus($event) {
    this.searchService.isMobileWidgetExpanded.set(true);
    this.searchWidgetManagerService.setFocusOnFreeSearchField();
    $event.stopPropagation();
  }

  protected expandMobileWidgetWithPlaceFocused($event): void {
    this.searchService.isMobileWidgetExpanded.set(true);

    const gtmTag = {
      event: GtmEventEnum.DEFAULT,
      Category: EventCategoryEnum.SEARCHNAV,
      Action: EventActionEnum.CHOOSE_LOCATION_BUTTON_CLICK,
      Label: 'click',
      Value: 1,
    };
    this.gtmService.pushTag(gtmTag);

    this.searchWidgetManagerService.setFocusOnPlacesField();
    this.searchWidgetManagerService.loadPlaces();
    $event.stopPropagation();
  }

  private updateFreeSearchFieldValues(state: SearchWidgetState): void {
    if (this.freeSearchForm.controls['term'].value !== state.autocompleteCurrentValue) {
      this.freeSearchForm.setValue(
        {
          term: state.autocompleteCurrentValue || '',
        },
        {emitEvent: false},
      );
    }
    this.isTermFreeSearchNotEmpty.set(this.freeSearchForm.controls['term'].value !== '');
  }

  private buildFreeSearchForm() {
    this.freeSearchForm = this.formBuilder.group({
      term: [''],
    });
  }
}
